import {
  APPLICATION_EVENT_TYPE,
} from '../../../../config/constants'

import type { TFunction } from 'i18next'

import {
  proposalAmountsEqual,
  proposalServiceLineTitleKey,
} from './proposal-service-line-utils'
import {
  formatProposalAmount,
} from './components/executor-proposal-card'

export type ApplicationProposalHistoryLineView = {
  key: string
  text: string
  hint?: string
  comment?: string
}

const PROPOSAL_HISTORY_EVENT_TYPES = new Set<string>([
  APPLICATION_EVENT_TYPE.PROPOSAL_PUBLISHED,
  APPLICATION_EVENT_TYPE.PROPOSAL_DECLINED,
])

type ProposalHistoryServiceLinePayload = {
  kind: string
  original_amount: string
  amount: string
  comment?: string
}

export function proposalHistoryEvents(
  events: ApplicationEvent[] | undefined,
): ApplicationEvent[] {
  if (!events?.length) return []
  return events
    .filter((event) => PROPOSAL_HISTORY_EVENT_TYPES.has(event.event_type))
    .sort(
      (left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime(),
    )
}

export function proposalPublishedEventLines(
  event: ApplicationEvent,
  t: TFunction,
): ApplicationProposalHistoryLineView[] {
  if (event.event_type !== APPLICATION_EVENT_TYPE.PROPOSAL_PUBLISHED) {
    return []
  }
  const payload = event.payload ?? {}
  const currencyCode = String(payload.currency_code || '')
  const rawLines = payload.service_lines
  if (!Array.isArray(rawLines) || !rawLines.length) {
    const amount = payload.amount
    if (amount) {
      const formatted = formatProposalAmount({ amount: String(amount), currencyCode })
      if (formatted) {
        return [ {
          key: 'total',
          text: t('applications__text__proposalHistoryPublishedTotal', { amount: formatted }),
        } ]
      }
    }
    return [ {
      key: 'generic',
      text: t('applications__text__proposalHistoryPublishedGeneric'),
    } ]
  }

  const views: ApplicationProposalHistoryLineView[] = []
  const lines = rawLines as ProposalHistoryServiceLinePayload[]
  let anyChanged = false

  for (const line of lines) {
    const label = t(proposalServiceLineTitleKey(line.kind))
    const from = formatProposalAmount({
      amount: line.original_amount,
      currencyCode,
    })
    const to = formatProposalAmount({
      amount: line.amount,
      currencyCode,
    })
    const changed = !proposalAmountsEqual(line.original_amount, line.amount)
    if (changed) {
      anyChanged = true
    }
    if (changed && from && to) {
      views.push({
        key: line.kind,
        text: t('applications__text__proposalHistoryLineChange', { label, from, to }),
        comment: line.comment?.trim() || undefined,
      })
    } else if (line.comment?.trim()) {
      views.push({
        key: `${line.kind}-comment`,
        text: `${label}: ${line.comment.trim()}`,
      })
    }
  }

  const totalAmount = payload.amount
  const totalFormatted = totalAmount
    ? formatProposalAmount({ amount: String(totalAmount), currencyCode })
    : null
  if (totalFormatted) {
    views.unshift({
      key: 'total',
      text: t('applications__text__proposalHistoryPublishedTotal', { amount: totalFormatted }),
      hint: anyChanged || payload.price_changed
        ? t('applications__text__proposalHistoryPublishedChanged')
        : t('applications__text__proposalHistoryPublishedUnchanged'),
    })
  } else if (!views.length) {
    views.push({
      key: 'generic',
      text: t('applications__text__proposalHistoryPublishedGeneric'),
    })
  }

  return views
}

export function proposalDeclinedEventReason(event: ApplicationEvent): string | null {
  if (event.event_type !== APPLICATION_EVENT_TYPE.PROPOSAL_DECLINED) {
    return null
  }
  const reason = event.message?.trim()
  return reason || null
}
